import type { AlertGroup, AlertKind, AlertSeverity, ComplianceAlertRow, ComplianceSurface, SignalId } from './complianceTypes';

/**
 * Contract-level rules of the Compliance console that the UI, the mock and (independently) the
 * backend all apply. Kept beside the types rather than in the feature so the mock layer never
 * imports from `features/`.
 */

/**
 * The app-side markers whose alerts are an AI-content matter (plan 1.1: A9 the assist-blocked burst,
 * A10 the withheld recaps, A11 the role-label leaks). Their kinds are `marker:<name>`.
 */
export const AI_CONTENT_MARKERS: readonly string[] = [
  'plan-my-day.assist-blocked',
  'plan-my-day.recap-withheld',
  'plan-my-day.day-summary-withheld',
  'plan-my-day.assist-role-leak',
  'plan-my-day.day-summary-role-leak',
];

/** Whether an alert kind is an AI-content matter: A9 to A11, A14 (`audit-escalation`) and A15 (`signal:*`). */
export function isAiContentKind(kind: AlertKind): boolean {
  if (kind === 'audit-escalation') return true;
  if (kind.startsWith('signal:')) return true;
  if (kind.startsWith('marker:')) return AI_CONTENT_MARKERS.includes(kind.slice('marker:'.length));
  return false;
}

/** The inbox's kind groups, in the order the quick filters and the grouped Kind select show them (display plan B2). */
export const ALERT_GROUPS: readonly AlertGroup[] = ['trail', 'copy', 'boundaries', 'checker', 'service', 'audit', 'traders', 'manual', 'unclassified'];

const GROUP_OF_KIND: Readonly<Record<string, AlertGroup>> = {
  'trail-tampered': 'trail', 'audit-bucket-configured': 'trail', 'audit-object-touched': 'trail', 'breakglass-assumed': 'trail',
  'export-task-failed': 'copy', 'export-completed-absent': 'copy',
  'marker:plan-my-day.assist-blocked': 'checker', 'marker:plan-my-day.recap-withheld': 'checker',
  'marker:plan-my-day.day-summary-withheld': 'checker', 'marker:plan-my-day.assist-role-leak': 'checker',
  'marker:plan-my-day.day-summary-role-leak': 'checker',
  'marker:problem-response': 'service', 'marker:me.export': 'service',
  'audit-escalation': 'audit', manual: 'manual', 'user-reviewed': 'manual',
};

/**
 * The group of an alert kind (display plan B2), the same table as the backend's `ComplianceRules.KindGroup`:
 * the audit trail (A1 to A4), the audit copy (A5 to A7), the boundaries (A8), the answer checker (A9 to A11),
 * the service (A12, A13), the monthly audit (A14), the traders (every `signal:*`), manual and review records,
 * and everything else (`aws-unclassified`, a marker nobody has named yet) as unclassified.
 */
export function kindGroup(kind: AlertKind): AlertGroup {
  const fixed = GROUP_OF_KIND[kind];
  if (fixed) return fixed;
  if (kind.startsWith('signal:')) return 'traders';
  if (kind.startsWith('marker:audit-export.')) return 'copy';
  if (kind.startsWith('marker:') && kind.endsWith('.prod-launch-blocked')) return 'boundaries';
  return 'unclassified';
}

/**
 * D14: any admin acknowledges; only the supervisor resolves an AI-content alert at p1 or p2, because
 * resolving one is a supervision act. The backend sends the answer on every row
 * (`resolveRequiresSupervisor`); this is the same rule for the mock and for a defensive re-check.
 */
export function requiresSupervisorToResolve(kind: AlertKind, severity: AlertSeverity): boolean {
  return isAiContentKind(kind) && severity !== 'p3';
}

/**
 * D14 on one alert, checked twice: the backend's `resolveRequiresSupervisor` on the row, OR the rule
 * above applied to its kind and severity. Either one saying "supervisor" disables the Resolve button, so
 * a backend that forgot the flag cannot hand a non-supervisor the act. The backend's 403 stays the boundary.
 */
export function resolveNeedsSupervisor(alert: Pick<ComplianceAlertRow, 'kind' | 'severity' | 'resolveRequiresSupervisor'>): boolean {
  return alert.resolveRequiresSupervisor || requiresSupervisorToResolve(alert.kind, alert.severity);
}

/**
 * Closing (resolving) an AI-content alert at p1 or p2 from this console. OFF (compliance review, 2026-09-27):
 * closing one is a supervision act that must rest on a read of the requests and replies, and the per-request
 * review that shows them is phase 3, not built. Until it exists the Resolve button renders disabled for EVERY
 * admin, the AI supervisor included, with the reason beside it; acknowledge and notes stay open. Frontend only:
 * the backend's D14 rule is unchanged, and turning this on restores the supervisor-only button as built.
 */
export const AI_CONTENT_RESOLVE_ENABLED = false;

/**
 * The AI-content kinds whose closing must rest on a read of the requests and replies (final review #8): S1, S2, S3,
 * S15 and S17 (answers held back or flagged, recaps), the checker markers (A9 to A11) and the monthly escalation
 * (A14). While `AI_CONTENT_RESOLVE_ENABLED` is false these cannot be closed from the console. The firm's own controls
 * and account facts (S8, S9, S10, S11, S20) are not a read of text: they close under D14 as built.
 */
const READ_TO_CLOSE_SIGNALS: readonly string[] = ['S1', 'S2', 'S3', 'S15', 'S17'];

export function closeNeedsRead(kind: AlertKind): boolean {
  if (kind === 'audit-escalation') return true;
  if (kind.startsWith('signal:')) return READ_TO_CLOSE_SIGNALS.includes(kind.slice('signal:'.length));
  if (kind.startsWith('marker:')) return AI_CONTENT_MARKERS.includes(kind.slice('marker:'.length));
  return false;
}

/** The signals the phase-2 evaluator lights (plan 4.3). Every other S-id renders as "not recorded yet". */
export const PHASE2_SIGNALS: readonly SignalId[] = [
  'S1', 'S2', 'S3', 'S8', 'S9', 'S10', 'S11', 'S15', 'S17', 'S20',
];

/**
 * The signals that never add points to a trader's score (compliance review, 2026-09-27), mirrored by the
 * backend's `RiskSignalCatalog.ScoreExempt`. S8, S9 and S20 are the firm's own control failures (a gate that let
 * a call through, a surface that should be dark, a record the firm cannot explain), not something the trader
 * did; S12 is the trader exercising a data right, which must never rank anyone higher. They still light and
 * still raise their alerts.
 */
export const SCORE_EXEMPT_SIGNALS: readonly SignalId[] = ['S8', 'S9', 'S12', 'S20'];

/** The seven surfaces of the overview, in the order the plan lists them. */
export const COMPLIANCE_SURFACES: readonly ComplianceSurface[] = [
  'day-assist-plan', 'day-assist-review', 'day-recap', 'day-recap-email', 'day-write-up',
  'backtest-parse', 'backtest-chat',
];

/** Recap surfaces: the final block rate reads as "recap withheld rate" there. */
export const RECAP_SURFACES: readonly ComplianceSurface[] = ['day-recap', 'day-recap-email', 'day-write-up'];

/** The ruleset 2026-09-24.2, as the checker names its rules; severity 1 = Block, 0 = Flag. */
export const CHECKER_RULES: readonly { ruleId: string; severity: 0 | 1 }[] = [
  { ruleId: 'system-prompt-echo',         severity: 1 },
  { ruleId: 'format-envelope-breach',     severity: 1 },
  { ruleId: 'control-override-claim',     severity: 1 },
  { ruleId: 'advisor-persona',            severity: 1 },
  { ruleId: 'broker-solicitation',        severity: 1 },
  { ruleId: 'advice-prescription',        severity: 1 },
  { ruleId: 'market-prediction',          severity: 1 },
  { ruleId: 'contradicted-day-total',     severity: 1 },
  { ruleId: 'review-banned-word',         severity: 0 },
  { ruleId: 'behavioural-labelling',      severity: 0 },
  { ruleId: 'generic-maxim',              severity: 0 },
  { ruleId: 'ungrounded-fact',            severity: 0 },
  { ruleId: 'unlabelled-reference-price', severity: 0 },
  { ruleId: 'invented-name',              severity: 0 },
  { ruleId: 'greeting-opener',            severity: 0 },
  { ruleId: 'role-label-leak',            severity: 0 },
];

/** The firm and test domains of D7 (`Supervision:FirmDomains`); the backend holds the list, this mirrors it for the mock. */
export const FIRM_DOMAINS: readonly string[] = ['optimusfutures.com', 'thicker.tech'];

/**
 * Words that name a customer's text (or the model's reply to it) when they appear in a field name.
 * `answers` (plural) is deliberately absent: `firstAnswersEnforce` is the collector's COUNT of first answers.
 */
const CUSTOMER_TEXT_WORDS: ReadonlySet<string> = new Set([
  'question', 'questions', 'reply', 'replies', 'answer', 'reasoning', 'excerpt', 'excerpts', 'transcript',
  'conversation', 'conversations', 'content', 'prompt', 'prompts', 'text', 'message', 'messages',
  'output', 'journal', 'chat',
]);

/** Suffixes that make a field an identifier or a measure of text, never the text itself (`snsMessageId`, `promptHash`). */
const IDENTIFIER_SUFFIX = /(Id|Ids|Hash|Sha256|Version|Count|At|Rate)$/;

/**
 * Whether a field NAME looks like it carries a customer's words (`question`, `replyText`, `reasoning`,
 * `excerpt`, `message`, ...). The console is content-blind: the contract has no such field (pinned by
 * `contentBlind.test.tsx`), and the one open-ended record it renders, an alert's reduced `payload`, drops
 * any key this matches rather than print it. Defence in depth: the backend's reduction is the control.
 */
export function isCustomerTextField(name: string): boolean {
  if (IDENTIFIER_SUFFIX.test(name)) return false;
  const words = name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[\s_.:-]+/)
    .map(w => w.toLowerCase())
    .filter(Boolean);
  return words.some(w => CUSTOMER_TEXT_WORDS.has(w));
}
